import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

const message = "Tienes cambios sin guardar. Si sales, se perderan. ¿Deseas salir?";

// BrowserRouter has no data-router blocker; keep this guard scoped to the report.
export default function useUnsavedReportChanges(hasChanges) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const dirtyRef = useRef(hasChanges);
  dirtyRef.current = hasChanges;

  useEffect(() => {
    let currentIndex = window.history.state?.idx;
    let restoringHistory = false;
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const confirmLeave = () => !dirtyRef.current || window.confirm(message);
    const wrapNavigation = (original) => (...args) => {
      if (!confirmLeave()) return;
      const result = original.apply(navigator, args);
      currentIndex = window.history.state?.idx;
      return result;
    };
    const guardedPush = wrapNavigation(originalPush);
    const guardedReplace = wrapNavigation(originalReplace);
    navigator.push = guardedPush;
    navigator.replace = guardedReplace;

    const beforeUnload = (event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const onPopState = (event) => {
      if (restoringHistory) {
        restoringHistory = false;
        event.stopImmediatePropagation();
        return;
      }
      const nextIndex = event.state?.idx;
      if (confirmLeave()) {
        currentIndex = nextIndex;
        return;
      }
      if (typeof currentIndex === "number" && typeof nextIndex === "number" && currentIndex !== nextIndex) {
        event.stopImmediatePropagation();
        restoringHistory = true;
        window.history.go(currentIndex - nextIndex);
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("popstate", onPopState, true);
    return () => {
      if (navigator.push === guardedPush) navigator.push = originalPush;
      if (navigator.replace === guardedReplace) navigator.replace = originalReplace;
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("popstate", onPopState, true);
    };
  }, [navigator]);

  return (remainingChanges) => { dirtyRef.current = remainingChanges; };
}
